import { useCallback, useState, memo, useMemo, useRef } from 'react'
import { GenericColumnModel, FormatterCell, RemoteOptionColumnModel, RemoteOptionsColumnModel } from '../models'
import styles from './body.module.scss'
import { EditableCell } from './EditableCell/EditableCell'
import { Tooltip } from '../Tooltip/Tooltip'
import { useCellFormatter } from './cellFormatter'
import { ErrorType } from 'components/Form'
import { Translation } from 'components/Translations/Translation'
import { SkeletonCell } from '../Skeleton/SkeletonCell'

type Props = {
	column: GenericColumnModel
	value: any
	columnWidth: number
	rowData: any
	cellEdited?: (rowData: any, columnId: string, value: any) => Promise<void>
	error: ErrorType | undefined
}

const CellWithoutMemo = ({ column, value, columnWidth, rowData, cellEdited, error }: Props) => {
	const contentRef = useRef<HTMLDivElement>(null);
	const [saving, setSaving] = useState(false);
	const [tooltipVisible, setTooltipVisible] = useState(false);

	const formatterCell: FormatterCell = useMemo(
		() => ({
			value,
			rowData,
			columnId: column.id
		}),
		[value, rowData, column.id]
	)

	const isCellEditable = useMemo(
		() => column.editable?.(formatterCell) || false,
		[column, formatterCell]
	)

	const clickCallback = useCallback(
		(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
			if (isCellEditable) {
				// don't select row
				e.stopPropagation();
			}
		},
		[isCellEditable]
	)

	const onChangeCompleteCallback = useCallback(
		async (columnId: string, value: any) => {
			if (cellEdited && rowData[columnId] !== value) {
				setSaving(true);
				await cellEdited(rowData, columnId, value);
				setSaving(false);
			}
		},
		[cellEdited, rowData]
	)

	const showTooltipCallback = useCallback(
		() => {
			const content = contentRef.current;
			if (!content) {
				return;
			}

			if (content.scrollWidth > content.clientWidth) {
				setTooltipVisible(true);
			}
		},
		[]
	)

	const hideTooltipCallback = useCallback(
		() => setTooltipVisible(false),
		[]
	)

	const cellFormatter = useCellFormatter();

	const formattedCell = useMemo(
		() => {
			const formatted = cellFormatter(column, formatterCell, isCellEditable);
			// if formatted is string, return translation of formatted
			if (typeof formatted === 'string') {
				return <Translation i18n={formatted} />
			}
			return formatted;
		},
		[column, formatterCell, isCellEditable, cellFormatter]
	)

	const className = `${styles.cell} ${isCellEditable ? styles.editing : ''} ${!!column.frozen ? styles.frozen : ''} ${error ? styles.error : ''}`;

	const styleMemo = useMemo(
		() => (!isCellEditable && column.getStyle) ? column.getStyle(value) : {},
		[value, column, isCellEditable]
	)

	const fetching = (column instanceof RemoteOptionColumnModel || column instanceof RemoteOptionsColumnModel) ? !!column.fetching : false;

	// data-type and data-columnid must always be present, so be aware when changing
	return (
		<div
			className={className}
			style={{ width: `${columnWidth}px`, ...styleMemo }}
			onClick={clickCallback}
			data-type='cell'
			data-body='true'
		>
			{isCellEditable && !fetching &&
				<EditableCell
					column={column}
					value={value}
					onChangeComplete={onChangeCompleteCallback}
					loading={saving}
					formatterCell={formatterCell}
				/>
			}
			{fetching &&
				<SkeletonCell />
			}
			<div
				ref={contentRef}
				className={`${styles.content} ${styles[`align_${column.align}`] || ''}`}
				style={{ visibility: (isCellEditable || fetching) ? 'hidden' : 'unset' }}
				onMouseOver={showTooltipCallback}
				onMouseLeave={hideTooltipCallback}
			>
				<span className={styles.inner} data-columnid={column.id}>
					{formattedCell}
				</span>
				{tooltipVisible && <Tooltip content={formattedCell} targetRef={contentRef} />}
			</div>
		</div>
	)
}

export const Cell = memo(CellWithoutMemo);
